import { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, useState } from 'react';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/cn';

interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> {
  label: string;
  error?: string;
  hint?: ReactNode;
}

export function TextField({ label, error, hint, id, ...props }: TextFieldProps) {
  const inputId = id ?? `tf-${label.replace(/\W+/g, '-').toLowerCase()}`;
  return (
    <label htmlFor={inputId} className="block">
      <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground font-medium mb-1.5">
        {label}
      </div>
      <input
        id={inputId}
        {...props}
        className={cn(
          'w-full h-11 px-3 rounded-lg border bg-card text-foreground transition-colors',
          'placeholder:text-muted-foreground/60',
          error ? 'border-destructive ring-2 ring-destructive/20' : 'border-border focus:border-accent-500',
        )}
      />
      {hint && !error && <div className="mt-1.5 text-xs text-muted-foreground">{hint}</div>}
      {error && <div className="mt-1.5 text-xs text-destructive">{error}</div>}
    </label>
  );
}

interface PasswordFieldProps extends Omit<TextFieldProps, 'type'> {
  showStrength?: boolean;
}

export function PasswordField({ label, error, showStrength, value, ...props }: PasswordFieldProps) {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const inputId = props.id ?? `pf-${label.replace(/\W+/g, '-').toLowerCase()}`;
  const strength = typeof value === 'string' ? scorePassword(value) : 0;

  return (
    <label htmlFor={inputId} className="block">
      <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground font-medium mb-1.5">
        {label}
      </div>
      <div className="relative">
        <input
          id={inputId}
          {...props}
          value={value}
          type={show ? 'text' : 'password'}
          className={cn(
            'w-full h-11 pl-3 pr-11 rounded-lg border bg-card text-foreground transition-colors',
            'placeholder:text-muted-foreground/60',
            error ? 'border-destructive ring-2 ring-destructive/20' : 'border-border focus:border-accent-500',
          )}
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-muted-foreground hover:text-foreground"
          aria-label={show ? t('auth.hidePassword') : t('auth.showPassword')}
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
      {showStrength && typeof value === 'string' && value.length > 0 && (
        <div className="mt-2">
          <div className="grid grid-cols-4 gap-1.5">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className={cn(
                  'h-1 rounded-full transition-colors',
                  i < strength
                    ? strength <= 1
                      ? 'bg-destructive'
                      : strength === 2
                      ? 'bg-warn-500'
                      : 'bg-emerald-500'
                    : 'bg-border',
                )}
              />
            ))}
          </div>
          <div className="mt-1.5 text-xs text-muted-foreground">
            {t(`auth.strength.${['veryWeak', 'weak', 'fair', 'good', 'strong'][strength]}`)}
          </div>
        </div>
      )}
      {error && <div className="mt-1.5 text-xs text-destructive">{error}</div>}
    </label>
  );
}

export function OrDivider({ label }: { label: string }) {
  return (
    <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-[0.18em] text-muted-foreground">
      <div className="flex-1 h-px bg-border" />
      <span>{label}</span>
      <div className="flex-1 h-px bg-border" />
    </div>
  );
}

export function FormError({ children }: { children: ReactNode }) {
  if (!children) return null;
  return (
    <div className="rounded-lg border border-destructive/40 bg-destructive/5 text-destructive text-sm px-3 py-2.5">
      {children}
    </div>
  );
}

export function SubmitButton({
  children,
  loading,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { loading?: boolean }) {
  return (
    <button
      type="submit"
      {...props}
      disabled={loading || props.disabled}
      className={cn(
        'w-full inline-flex items-center justify-center gap-2 h-11 rounded-lg font-medium text-sm transition-colors',
        'bg-foreground text-background hover:bg-foreground/90 disabled:opacity-60',
      )}
    >
      {loading && <Loader2 size={16} className="animate-spin" />}
      {children}
    </button>
  );
}

/** Score 0..4 — 0 very weak, 4 strong. */
export function scorePassword(value: string): number {
  if (!value) return 0;
  let score = 0;
  if (value.length >= 8) score++;
  if (value.length >= 12) score++;
  if (/[a-z]/.test(value) && /[A-Z]/.test(value)) score++;
  if (/\d/.test(value) && /[^a-zA-Z0-9]/.test(value)) score++;
  return Math.min(4, score);
}
